/**
 * The shell shared by the Veeva and Blob browsers: a page header, a connection
 * picker, and whichever browser the chosen connection needs.
 *
 * **Why browsing is its own destination.** It used to live behind Integrations
 * — open the connections list, open *a* connection, press Browse. That made
 * looking at data a sub-task of administering a connection, which is backwards:
 * configuring a vault is a rare, admin-shaped act, while browsing it is the
 * everyday one. The browsers are now top-level nav items and the connection is
 * a dropdown inside them, so switching between two Blob accounts is one select
 * rather than two navigations.
 *
 * **The connection lives in the URL** (`/browse/blob/:connectionId`), not in
 * component state, so a browse view can be refreshed, bookmarked and shared.
 * Picking from the dropdown replaces the URL rather than pushing, so Back
 * leaves the browser instead of walking you through every connection you
 * looked at.
 */

import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Group, Select, Stack, Text, Title } from "@mantine/core";
import { IconPlus, IconPlugConnected } from "@tabler/icons-react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { listConnections, type ConnectionOut } from "@/shared/api/integration";
import { toastApiError } from "@/shared/ui/toast";

interface Props {
  /** `veeva` | `blob` — the connection source type this page browses. */
  sourceType: string;
  title: string;
  description: string;
  /** Rendered once a connection is chosen. */
  children: (connectionId: string) => React.ReactNode;
}

export function ConnectionBrowserPage({ sourceType, title, description, children }: Props) {
  const { connectionId = "" } = useParams();
  const navigate = useNavigate();
  const getToken = useAccessToken();
  const [connections, setConnections] = useState<ConnectionOut[]>([]);
  const [loading, setLoading] = useState(true);

  const select = useCallback(
    (id: string) => navigate(`/browse/${sourceType}/${id}`, { replace: true }),
    [navigate, sourceType],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await listConnections(token, sourceType)).items;
        if (cancelled) return;
        setConnections(items);
        // With exactly one connection there is no choice to make, so make it —
        // a dropdown with a single option is a click that teaches nothing.
        if (!connectionId && items.length === 1 && items[0]) {
          navigate(`/browse/${sourceType}/${items[0].connection_id}`, { replace: true });
        }
      } catch (err) {
        if (!cancelled) toastApiError("Could not load connections", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `connectionId` is deliberately absent: this loads the list once, and
    // re-running it on every selection would refetch for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken, sourceType, navigate]);

  const options = connections.map((c) => ({ value: c.connection_id, label: c.display_name }));
  const chosen = connections.find((c) => c.connection_id === connectionId) ?? null;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2}>{title}</Title>
          <Text c="dimmed" size="sm">
            {description}
          </Text>
        </div>
        <Select
          label="Connection"
          placeholder={loading ? "Loading…" : "Pick a connection"}
          data={options}
          value={connectionId || null}
          onChange={(value) => value && select(value)}
          disabled={loading || options.length === 0}
          searchable={options.length > 5}
          allowDeselect={false}
          w={280}
        />
      </Group>

      {!loading && options.length === 0 && (
        <Alert icon={<IconPlugConnected size={16} />} variant="light">
          <Stack gap="xs" align="flex-start">
            <Text size="sm">
              There are no {title.toLowerCase()} connections yet. Connections are set up once under
              Integrations, then browsed from here.
            </Text>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              component={RouterLink}
              to="/integration/connections/new"
            >
              Add a connection
            </Button>
          </Stack>
        </Alert>
      )}

      {!loading && options.length > 0 && !chosen && (
        <Alert variant="light">
          <Text size="sm">Pick a connection above to start browsing.</Text>
        </Alert>
      )}

      {/* Keyed on the connection so switching remounts the browser rather than
          leaving the previous connection's containers, query results or
          breadcrumb on screen while the new one loads. */}
      {chosen && <div key={chosen.connection_id}>{children(chosen.connection_id)}</div>}
    </Stack>
  );
}
